/**
 * QuemFezSection — "Quem fez o quê" e "O que é determinístico (não é agente)" na
 * Conferência e na Saída (SPEC-AGENTES-A5 FR-17).
 *
 * Mostra o que o relatório de garantia da entrega já diz (FR-16), sem cálculo na tela:
 * a tabela etapa → agente → versão do prompt → tier → modelo → custo → delegações →
 * achados, e a declaração do que não depende de prompt nem de modelo.
 *
 * `relatorio` null/undefined → não renderiza nada (entrega sem relatório de garantia).
 */
import { C } from '@/theme/palette';
import {
  TITULO_QUEM_FEZ, TITULO_DETERMINISTICO, useRelatorioDeGarantia,
} from '@/api/relatorioDeGarantia';

const th = { textAlign: 'left', padding: '6px 8px', fontSize: 11, fontWeight: 700,
  color: C.neutralMuted, borderBottom: `1px solid ${C.blackBorder ?? C.border}`,
  whiteSpace: 'nowrap' };
const td = { padding: '6px 8px', fontSize: 12, color: C.neutralDim, verticalAlign: 'top' };

export function QuemFezSection({ relatorio }) {
  if (!relatorio) return null;
  const { colunas = [], linhas = [], deterministico = '', deterministicos = [] } = relatorio;
  return (
    <section aria-label="Quem fez o quê" data-testid="quem-fez-o-que"
      style={{ marginTop: 16, marginBottom: 28 }}>
      <h2 style={{ fontSize: 14, fontWeight: 700, color: C.neutral, margin: '0 0 4px' }}>
        {TITULO_QUEM_FEZ}
      </h2>
      <p style={{ fontSize: 12, color: C.neutralDim, margin: '0 0 10px' }}>
        Lido do relatório de garantia da entrega (reports/software-assurance-report.md):
        cada etapa, o agente que a executou, a versão do prompt, o tier, o modelo resolvido,
        o custo, as delegações a subagentes e os achados das revisões.
      </p>
      {linhas.length > 0 ? (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>{colunas.map((c) => <th key={c} scope="col" style={th}>{c}</th>)}</tr>
            </thead>
            <tbody>
              {linhas.map((l, i) => (
                <tr key={i}>
                  {l.map((cel, j) => (
                    <td key={j} style={{ ...td, ...(j === 1 ? { fontFamily: 'monospace',
                      color: C.neutral } : {}) }}>{cel}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p style={{ fontSize: 12, color: C.neutralMuted, margin: 0 }}>
          O relatório desta entrega não traz o quadro de agentes (entrega anterior aos
          agentes especialistas, ou o quadro não pôde ser montado).
        </p>
      )}

      {(deterministico || deterministicos.length > 0) && (
        <div data-testid="deterministicos" style={{ marginTop: 16 }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, color: C.neutral, margin: '0 0 4px' }}>
            {TITULO_DETERMINISTICO}
          </h3>
          {deterministico && (
            <p style={{ fontSize: 12, color: C.neutralDim, margin: 0 }}>{deterministico}</p>
          )}
          {deterministicos.length > 0 && (
            <ul style={{ fontSize: 12, color: C.neutralDim, margin: '4px 0 0', paddingLeft: 18 }}>
              {deterministicos.map((d) => <li key={d}>{d}</li>)}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

/** A seção ligada ao relatório de garantia da execução (busca o `.md` da entrega). */
export function QuemFezDaExecucao({ executionId }) {
  const { data } = useRelatorioDeGarantia(executionId);
  return <QuemFezSection relatorio={data} />;
}
